import React from "react";

export const STATUS_STYLES = {
  open: { bg: "#EFF6FF", color: "#1D4ED8", dot: "#3B82F6", border: "#BFDBFE" },
  in_progress: { bg: "#FFF7ED", color: "#C2410C", dot: "#F97316", border: "#FED7AA" },
  resolved: { bg: "#F0FDF4", color: "#15803D", dot: "#22C55E", border: "#BBF7D0" },
  closed: { bg: "#F9FAFB", color: "#6B7280", dot: "#9CA3AF", border: "#E5E7EB" },
  deleted: { bg: "#FEF2F2", color: "#991B1B", dot: "#EF4444", border: "#FECACA" },
  active: { bg: "#F0FDF4", color: "#15803D", dot: "#22C55E", border: "#BBF7D0" },
  inactive: { bg: "#FEF2F2", color: "#DC2626", dot: "#EF4444", border: "#FECACA" },
  degraded: { bg: "#FFF7ED", color: "#C2410C", dot: "#F97316", border: "#FED7AA" },
  down: { bg: "#FEF2F2", color: "#991B1B", dot: "#DC2626", border: "#FECACA" },
  healthy: { bg: "#F0FDF4", color: "#15803D", dot: "#22C55E", border: "#BBF7D0" },
  warning: { bg: "#FFF7ED", color: "#C2410C", dot: "#F59E0B", border: "#FED7AA" },
  critical: { bg: "#FEF2F2", color: "#DC2626", dot: "#EF4444", border: "#FECACA" },
  unknown: { bg: "#F9FAFB", color: "#6B7280", dot: "#9CA3AF", border: "#E5E7EB" },
  running: { bg: "#F0FDF4", color: "#15803D", dot: "#22C55E", border: "#BBF7D0" },
  stopped: { bg: "#FEF2F2", color: "#DC2626", dot: "#EF4444", border: "#FECACA" },
  decommissioned: { bg: "#F1F5F9", color: "#64748B", dot: "#94A3B8", border: "#E2E8F0" },
};

export function getStatusStyle(status) {
  const key = String(status || "").toLowerCase().trim();
  const s = STATUS_STYLES[key] || STATUS_STYLES.unknown;
  return {
    ...s,
    text: s.color,
  };
}

export function isServiceRunning(svc) {
  if (!svc) return false;
  const status = String(svc.raw_status || svc.status || "").toLowerCase().trim();
  if (
    status === "inactive" ||
    status === "stopped" ||
    status === "down" ||
    status === "offline" ||
    status === "failed" ||
    status === "error"
  ) {
    return false;
  }
  return true;
}

/**
 * Authoritative Status Resolver for Applications & Sub-Applications.
 * True-to-telemetry: verifies actual running state of attached services & recursive child apps.
 */
export function getApplicationStatus(app) {
  if (!app) return "inactive";

  // 1. Explicitly decommissioned / archived / disabled
  if (app.decommissioned_at || app.is_active === false) {
    return "inactive";
  }

  const rawStatus = String(app.status || "").toLowerCase().trim();
  if (
    rawStatus === "decommissioned" ||
    rawStatus === "stopped" ||
    rawStatus === "down" ||
    rawStatus === "offline" ||
    rawStatus === "failed" ||
    rawStatus === "error"
  ) {
    return "inactive";
  }

  // 2. Explicit degraded / warning / critical
  if (rawStatus === "critical") {
    return "critical";
  }
  if (rawStatus === "degraded" || rawStatus === "warning") {
    return "degraded";
  }
  if (rawStatus === "healthy" || rawStatus === "active" || rawStatus === "running") {
    return "active";
  }

  // 3. Evaluate direct attached services if present
  const directServices = app.services || [];
  const totalDirect = directServices.length;
  const runningDirect = directServices.filter(isServiceRunning).length;

  // 4. Evaluate child sub-applications recursively if present
  const subApps = app.sub_applications || [];
  const totalSubApps = subApps.length;
  const activeSubApps = subApps.filter((sub) => {
    const subStatus = getApplicationStatus(sub);
    return subStatus === "active" || subStatus === "healthy";
  }).length;

  if (totalDirect > 0 || totalSubApps > 0) {
    const isPartiallyDown =
      (totalDirect > 0 && runningDirect < totalDirect) ||
      (totalSubApps > 0 && activeSubApps < totalSubApps);
    if (isPartiallyDown) {
      return "degraded";
    }
    return "active";
  }

  return "active";
}

/**
 * Calculates authoritative application health score (0 - 100%).
 * Evaluates live telemetry, incident ticket penalties, and sub-app health.
 */
export function getApplicationHealthScore(app) {
  if (!app) return 0;
  const status = getApplicationStatus(app);
  if (status === "inactive" || status === "decommissioned") {
    return 0;
  }

  const rawScore = Number(app.health_score);
  const openTickets = Number(app.open_tickets_count || 0);

  // If backend provided a valid positive score and no active degradation
  if (!isNaN(rawScore) && rawScore > 0) {
    return Math.max(0, Math.min(100, Math.round(rawScore)));
  }

  // Live telemetry dynamic scoring
  if (status === "active" || status === "healthy") {
    const penalty = Math.min(50, openTickets * 15);
    return Math.max(50, 100 - penalty);
  }

  if (status === "degraded") {
    const penalty = Math.min(40, openTickets * 10);
    return Math.max(30, 75 - penalty);
  }

  if (status === "critical") {
    return Math.max(10, 40 - Math.min(30, openTickets * 10));
  }

  return 0;
}

// Backward-compatible alias
export const getApplicationUnifiedStatus = getApplicationStatus;

const normalizeAssetStatus = (value) => {
  const status = String(value || "").toLowerCase().trim();
  if (!status) return null;
  if (["offline", "stopped", "down"].includes(status)) return "inactive";
  if (["healthy", "running"].includes(status)) return "active";
  if (status === "warning") return "degraded";
  if (status === "error") return "critical";
  return status;
};

const getApiAssetStatus = (asset) => {
  if (!asset) return "unknown";

  const statuses = [
    normalizeAssetStatus(asset.health_status),
    normalizeAssetStatus(asset.status),
  ].filter(Boolean);

  if (statuses.includes("decommissioned")) return "decommissioned";
  if (statuses.includes("inactive")) return "inactive";

  const recognizedStatuses = ["active", "degraded", "critical"];
  const healthStatus = normalizeAssetStatus(asset.health_status);
  if (recognizedStatuses.includes(healthStatus)) return healthStatus;

  const status = normalizeAssetStatus(asset.status);
  if (recognizedStatuses.includes(status)) return status;

  return healthStatus || status || "unknown";
};

/** Resolve server status only from status fields returned by the API. */
export function getServerStatus(server) {
  return getApiAssetStatus(server);
}

/**
 * Authoritative Status Resolver for Network Devices & Routers.
 * Evaluates SNMP responsiveness, ICMP ping sweep, and reachability.
 */
export function getRouterStatus(router) {
  return getApiAssetStatus(router);
}

/**
 * Master Polymorphic Status Resolver:
 * Intelligently determines entity type and invokes the correct authoritative resolver.
 */
export function getUnifiedEntityStatus(entity, explicitType = null) {
  if (!entity) return "inactive";

  // If a string status was passed directly
  if (typeof entity === "string") {
    const s = entity.toLowerCase().trim();
    if (s === "running" || s === "healthy") return "active";
    if (s === "stopped" || s === "down") return "inactive";
    return s || "inactive";
  }

  // Explicit type handling
  if (explicitType === "application") return getApplicationStatus(entity);
  if (explicitType === "server") return getServerStatus(entity);
  if (explicitType === "router" || explicitType === "network") return getRouterStatus(entity);
  if (explicitType === "service") return isServiceRunning(entity) ? "active" : "inactive";

  // Polymorphic shape detection:
  // 1. Application entity (has sub_applications or services or is_parent)
  if ("sub_applications" in entity || "is_parent" in entity || ("services" in entity && "owner_team" in entity)) {
    return getApplicationStatus(entity);
  }

  // 2. Router entity (has snmp_port or ip_address or is_responsive in specs)
  if ("snmp_port" in entity || entity.specs?.is_responsive !== undefined || entity.specs?.snmp_status !== undefined) {
    return getRouterStatus(entity);
  }

  // 3. Server entity (has hostname, os, or server specs)
  if ("hostname" in entity || "os" in entity || "hosted_on" in entity) {
    return getServerStatus(entity);
  }

  // 4. Service entity (has service_type, raw_status, or port)
  if ("service_type" in entity || "raw_status" in entity || "service_name" in entity) {
    return isServiceRunning(entity) ? "active" : "inactive";
  }

  // Default fallback
  const fallback = String(entity.status || "").toLowerCase().trim();
  return fallback || "inactive";
}

const PRIORITY_STYLES = {
  critical: { bg: "#FEF2F2", color: "#DC2626", border: "#FECACA" },
  high: { bg: "#FFF7ED", color: "#EA580C", border: "#FED7AA" },
  medium: { bg: "#FEFCE8", color: "#CA8A04", border: "#FEF08A" },
  low: { bg: "#F0FDF4", color: "#16A34A", border: "#BBF7D0" },
};

const TIER_STYLES = {
  tier_1: { bg: "#F5F3FF", color: "#7C3AED", border: "#DDD6FE", label: "Tier 1 — Mission Critical" },
  tier_2: { bg: "#EFF6FF", color: "#2563EB", border: "#BFDBFE", label: "Tier 2 — Business Core" },
  tier_3: { bg: "#F0FDFA", color: "#0D9488", border: "#99F6E4", label: "Tier 3 — Supporting" },
  1: { bg: "#F5F3FF", color: "#7C3AED", border: "#DDD6FE", label: "Tier 1" },
  2: { bg: "#EFF6FF", color: "#2563EB", border: "#BFDBFE", label: "Tier 2" },
  3: { bg: "#F0FDFA", color: "#0D9488", border: "#99F6E4", label: "Tier 3" },
};

export function StatusBadge({ status, entity, type, style = {} }) {
  const effectiveStatus = entity ? getUnifiedEntityStatus(entity, type) : status;
  const s = getStatusStyle(effectiveStatus);
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "3px 9px",
        borderRadius: 20,
        background: s.bg,
        color: s.color,
        border: `1px solid ${s.border || s.bg}`,
        fontSize: 11,
        fontWeight: 600,
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      <span
        style={{
          width: 5,
          height: 5,
          borderRadius: "50%",
          background: s.dot,
          flexShrink: 0,
        }}
      />
      {effectiveStatus ? String(effectiveStatus).replace(/_/g, " ") : "unknown"}
    </span>
  );
}

export function PriorityBadge({ priority, style = {} }) {
  const p = PRIORITY_STYLES[priority?.toLowerCase()] || PRIORITY_STYLES.medium;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "3px 9px",
        borderRadius: 20,
        background: p.bg,
        color: p.color,
        fontSize: 11,
        fontWeight: 600,
        border: `1px solid ${p.border}`,
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      {priority || "Medium"}
    </span>
  );
}

export function TierBadge({ tier, label, style = {} }) {
  const key = String(tier || "").toLowerCase().replace(/[^a-z0-9]/g, "_");
  const t = TIER_STYLES[key] || TIER_STYLES[tier] || {
    bg: "#F8FAFC",
    color: "#475569",
    border: "#E2E8F0",
    label: `Tier ${tier || "—"}`,
  };

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "3px 8px",
        borderRadius: 12,
        background: t.bg,
        color: t.color,
        fontSize: 11,
        fontWeight: 600,
        border: `1px solid ${t.border}`,
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      {label || t.label}
    </span>
  );
}

export function HealthBadge({ score, percent, style = {} }) {
  const val = percent !== undefined ? percent : score;
  const num = Number(val);
  const isValid = Number.isFinite(num) && val !== null && val !== undefined;

  let bg = "#F8FAFC";
  let color = "#64748B";
  let border = "#E2E8F0";

  if (isValid) {
    if (num >= 90) {
      bg = "#F0FDF4";
      color = "#15803D";
      border = "#BBF7D0";
    } else if (num >= 50) {
      bg = "#FFFBEB";
      color = "#B45309";
      border = "#FDE68A";
    } else {
      bg = "#FEF2F2";
      color = "#DC2626";
      border = "#FECACA";
    }
  }

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "2px 8px",
        borderRadius: 12,
        background: bg,
        color: color,
        border: `1px solid ${border}`,
        fontSize: 11.5,
        fontWeight: 700,
        ...style,
      }}
    >
      {isValid ? `${num.toFixed(1)}%` : "—"}
    </span>
  );
}

export function TagBadge({ label, color, bg, style = {} }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 7px",
        borderRadius: 4,
        background: bg || "#F1F5F9",
        color: color || "#475569",
        fontSize: 11,
        fontWeight: 500,
        border: "1px solid #E2E8F0",
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      {label}
    </span>
  );
}

export default StatusBadge;
